import { useLocation, useNavigate } from 'react-router';
import { MouseEvent } from 'react';
import {
    Briefcase20Filled,
    Briefcase20Regular,
    BriefcaseFilled,
    BriefcaseRegular,
    bundleIcon,
    PuzzleCubePieceFilled,
    PuzzleCubePieceRegular,
} from '@fluentui/react-icons';
import { makeStyles, Tab, TabList, tokens, Title2, mergeClasses } from '@fluentui/react-components';
import { useAccess } from '../../../auth/useAccess';
import { UserAccount } from '../../../data/types';
import { useAccountState } from '../../../redux/reducers/accountReducer';
import { Column, Row, BrandCard, Paths } from '../../common';
import { useCommonStyles } from '../../common/Styles/commonStyles';
import { AccountSettingsCaseData } from '../Settings/AccountSettingsCaseData';
import { EngagementTemplatesPage } from './EngagementTemplatesPage';
import { CaseTemplatesPage } from './CaseTemplatesPage';
import { useEffect } from 'react';
import { appTokens } from '../../common/Styles/appTokens';
import { useMediaSizes } from '../../common/Styles/useMediaSizes';

import { CWTabList } from '../../common/CWTabList';

export enum TemplatesTabs {
    caseData = 'caseData',
    engagementTemplates = 'engTemplates',
    caseTemplates = 'caseTemplates',
}

const useStyles = makeStyles({
    tabList: {},
    tab: {
        width: '100%',
        height: '2.5rem',
        ':hover': {
            backgroundColor: `${tokens.colorNeutralBackground2Hover} !important`,
        },
    },
});

export const TemplatesPage = () => {
    //Hooks
    const { primaryAccount } = useAccountState();
    const { user, account } = (primaryAccount as UserAccount) ?? {};
    const access = useAccess(user);
    const navTo = useNavigate();
    const { pathname, state } = useLocation();
    const selectedTab = (state?.tab as TemplatesTabs) || TemplatesTabs.caseTemplates;
    const mediaSizes = useMediaSizes();

    //Logic
    const switchTab = (newTab: TemplatesTabs) => {
        navTo(pathname, { state: { tab: newTab } });
    };

    //Effects
    useEffect(() => {
        if (!account?.features?.templates) {
            navTo(Paths.index);
        }
    }, []);

    if (!account?.features?.templates) {
        return null;
    }

    return (
        <Column gap='medium' height={appTokens.pageHeight()}>
            <Row gap='medium' height='auto' width='auto'>
                <Column flex='0 0 auto' vAlign='middle'>
                    <PuzzleCubePieceRegular style={{ fontSize: '2.25em' }} />
                </Column>
                <Column flex='1 1 auto' vAlign='middle' align='left'>
                    <Title2>Templates</Title2>
                </Column>
                {mediaSizes.isUnderLargeLaptop && (
                    <Column flex='1 1 auto' padding='pl-2' vAlign='middle' align='right'>
                        <TemplatesNav selectedTab={selectedTab} switchTab={switchTab} vertical={false} size='medium' />
                    </Column>
                )}
            </Row>
            <Row noWrap flex='1'>
                {!mediaSizes.isUnderLargeLaptop && (
                    <Column width='auto' padding='pt-2'>
                        <TemplatesNav
                            selectedTab={selectedTab}
                            vertical
                            size={mediaSizes.isUnderDesktop ? 'medium' : 'large'}
                            switchTab={switchTab}
                        />
                    </Column>
                )}
                <Column flex='1' padding='pb-1'>
                    <BrandCard flex='1'>
                        {selectedTab == TemplatesTabs.caseData && !!access.templates.canSeeTemplates() && (
                            <>
                                <AccountSettingsCaseData />
                            </>
                        )}
                        {selectedTab == TemplatesTabs.engagementTemplates &&
                            !!access.templates.canSeeEngagementTemplates() && (
                                <>
                                    <EngagementTemplatesPage />
                                </>
                            )}
                        {selectedTab == TemplatesTabs.caseTemplates && !!access.templates.canSeeCaseTemplates() && (
                            <>
                                <CaseTemplatesPage />
                            </>
                        )}
                    </BrandCard>
                </Column>
            </Row>
        </Column>
    );
};

type TemplatesTabsProps = {
    selectedTab: TemplatesTabs;
    vertical?: boolean;
    collapsed?: boolean;
    size?: 'small' | 'medium' | 'large';
    switchTab?: (tab: TemplatesTabs) => void;
};
const TemplatesNav = (props: TemplatesTabsProps) => {
    const { selectedTab, vertical = false, collapsed = false, size = 'large', switchTab } = props;

    //Hooks
    const { primaryAccount } = useAccountState();
    const access = useAccess(primaryAccount?.user);

    //Icons
    const BriefcaseIcons = bundleIcon(BriefcaseFilled, BriefcaseRegular);
    const PuzzleCubeIcons = bundleIcon(PuzzleCubePieceFilled, PuzzleCubePieceRegular);

    const tabs = [
        {
            value: TemplatesTabs.caseData,
            icon: <BriefcaseIcons />,
            label: 'Case Data Types',
            onClick: () => switchTab?.(TemplatesTabs.caseData),
        },
        {
            value: TemplatesTabs.engagementTemplates,
            icon: <PuzzleCubeIcons />,
            label: 'Engagement Templates',
            onClick: () => switchTab?.(TemplatesTabs.engagementTemplates),
            access: access.templates.canSeeEngagementTemplates(),
        },
        {
            value: TemplatesTabs.caseTemplates,
            icon: <PuzzleCubeIcons />,
            label: 'Case Templates',
            onClick: () => switchTab?.(TemplatesTabs.caseTemplates),
            access: access.templates.canSeeCaseTemplates(),
        },
    ];

    return (
        <CWTabList
            selectedTab={selectedTab}
            vertical={vertical}
            collapsed={collapsed}
            size={size}
            dataTestId='templates'
            tabs={tabs}
        />
    );
};
